// /terminal — operator landing.
// Top: ObjectHeader (greeting, system pills, FY metrics) — real data from /v1/stats.
// Body: nested Surface — horizontal split with two vertical sub-Surfaces.
// Layout is owned by @eidollon/ui's Surface/Pane primitives (auto-persists to
// localStorage). Each Surface registers itself with SurfaceReset so the
// header's "RESET LAYOUT" button can clear all three at once.

import { apiFetch } from '@/lib/api';
import { compactUsd, dateOnly, relTime, ts } from '@/lib/format';
import { type OnYourRadarRow, type SinceLastLookedRow, useBriefData } from '@/lib/useBriefData';
import { EmptyState, ObjectHeader, Pill } from '@/primitives';
import { useAuth } from '@/state/auth';
import type { filters as f } from '@eidollon/contracts';
import { Pane, Surface, SurfaceReset } from '@eidollon/ui';
import { encode } from '@eidollon/ui-filters';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authed/terminal')({
  component: ScreenLanding,
});

interface StatsOverview {
  totals: {
    tenders: number;
    awards: number;
    contracts: number;
    vendors: number;
    agencies: number;
    standing_offers: number;
    disclosures: number;
  };
  by_jurisdiction: Array<{ jurisdiction: string; count: number }>;
  active_now: number;
}

interface PipelineStats {
  pipeline_12m: { value: number; currency: string } | null;
  closing_30d: number;
  closing_90d: number;
  newest_award: string | null;
}

interface AwardRow {
  id: string;
  publication_date: string;
  jurisdiction: string;
  vendor_name: string | null;
  agency_raw_name: string | null;
  contract_value: number | null;
  total_contract_value: number | null;
  award_date: string | null;
  title: string | null;
  status: string;
}

interface SavedScreen {
  id: string;
  name: string;
  description: string | null;
  filters: Record<string, unknown>;
  last_run_at: string | null;
  last_result_count: number | null;
  is_shared: boolean;
}

// Surface ids — bare strings; the Surface primitive prefixes with `layout.`
// when reading/writing localStorage. Listed here so SurfaceReset can clear
// them all in one click.
const SURFACE_IDS = ['terminal.h', 'terminal.lv', 'terminal.rv'];

function ScreenLanding() {
  const { t } = useTranslation();
  const me = useAuth((s) => s.me);
  const accessToken = useAuth((s) => s.accessToken);
  const greetingKey = useGreetingKey();

  const enabled = !!accessToken;

  const stats = useQuery({
    enabled,
    queryKey: ['stats', 'overview'],
    queryFn: () => apiFetch<StatsOverview>('/v1/stats/overview'),
  });
  const pipeline = useQuery({
    enabled,
    queryKey: ['stats', 'pipeline'],
    queryFn: () => apiFetch<PipelineStats>('/v1/stats/pipeline'),
  });
  const recentAwards = useQuery({
    enabled,
    queryKey: ['awards', 'recent'],
    queryFn: () => apiFetch<{ items: AwardRow[] }>('/v1/awards?limit=15'),
  });
  const screens = useQuery({
    enabled,
    queryKey: ['screens'],
    queryFn: () => apiFetch<{ items: SavedScreen[] }>('/v1/screens'),
  });

  if (!accessToken) {
    return (
      <div
        style={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="progress" />
        <span className="meta upper mono">{t('home.session.resolving')}</span>
      </div>
    );
  }

  const totals = stats.data?.totals;
  const greetName = me?.display_name ?? me?.email ?? null;
  const headerTitle = greetName
    ? t(`home.greeting.${greetingKey}`, { name: greetName })
    : t('home.greeting.fallbackTitle');

  const headerSub = t('home.headerSub', {
    tenant: me?.tenant_name ?? '—',
    role: me?.role.replace('_', ' ').toUpperCase() ?? '',
    classification: me?.classification ?? 'PUBLIC',
  });

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {(stats.isLoading || pipeline.isLoading) && <div className="progress" />}

      <ObjectHeader
        type="CASE"
        id="WORKSPACE"
        title={headerTitle}
        sub={headerSub}
        pills={[
          { tone: 'green', label: 'STREAM' },
          { tone: 'primary', label: `${ts(stats.data?.active_now ?? 0)} ACTIVE` },
          { tone: 'amber', label: `${pipeline.data?.closing_30d ?? 0} CLOSING <30D` },
        ]}
        metrics={[
          { k: 'TENDERS', v: ts(totals?.tenders ?? 0) },
          { k: 'AWARDS', v: ts(totals?.awards ?? 0) },
          { k: 'CONTRACTS', v: ts(totals?.contracts ?? 0) },
          { k: 'PIPELINE 12M', v: compactUsd(pipeline.data?.pipeline_12m?.value ?? null) },
        ]}
        actions={<SurfaceReset surfaceIds={SURFACE_IDS} />}
      />

      {/* Resizable 4-pane workspace */}
      <Surface id="terminal.h" direction="horizontal">
        {/* Left column — personal brief (top) + recent awards (bottom) */}
        <Pane id="h-left" defaultSize={62} minSize={20}>
          <Surface id="terminal.lv" direction="vertical">
            <Pane
              id="lv-top"
              defaultSize={55}
              minSize={15}
              collapsible
              title="MY BRIEF"
              sub="personalize this terminal"
            >
              <PersonalBriefPane />
            </Pane>
            <Pane
              id="lv-bot"
              defaultSize={45}
              minSize={15}
              collapsible
              title="RECENT AWARDS"
              sub={`${recentAwards.data?.items.length ?? 0} items`}
            >
              <RecentAwardsPane query={recentAwards} />
            </Pane>
          </Surface>
        </Pane>

        {/* Right column — saved screens (top) + pipeline (bottom) */}
        <Pane id="h-right" defaultSize={38} minSize={15}>
          <Surface id="terminal.rv" direction="vertical">
            <Pane
              id="rv-top"
              defaultSize={50}
              minSize={15}
              collapsible
              title="SAVED SCREENS"
              sub={`${screens.data?.items.length ?? 0} total`}
            >
              <SavedScreensPane query={screens} />
            </Pane>
            <Pane
              id="rv-bot"
              defaultSize={50}
              minSize={15}
              collapsible
              title="PIPELINE / DISTRIBUTION"
            >
              <PipelinePane stats={stats.data} pipeline={pipeline.data} />
            </Pane>
          </Surface>
        </Pane>
      </Surface>
    </div>
  );
}

function useGreetingKey(): 'morning' | 'afternoon' | 'evening' {
  const h = new Date().getHours();
  if (h < 5) return 'evening';
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

// ── Personal Brief — top-left ──────────────────────────────────────────────
// Three independent strips, each backed by GET /v1/me/brief. Each strip
// falls back to teaching copy when its slice is empty — partial deployment
// (one strip live, two empty) looks intentional, not broken. Phase 2 only
// lights up "ON YOUR RADAR"; SINCE YOU LAST LOOKED + MARKET PULSE wait on
// Phases 3-4 (entity-change publisher + intel-fusion worker).
function PersonalBriefPane() {
  const brief = useBriefData();
  return (
    <div
      style={{
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      }}
    >
      <BriefStrip
        label="SINCE YOU LAST LOOKED"
        countSuffix={
          brief.isEmpty.sll ? null : `${brief.data?.since_last_looked.length ?? 0} changes`
        }
      >
        {brief.isLoading ? (
          <BriefTeachingCopy>loading…</BriefTeachingCopy>
        ) : brief.isEmpty.sll ? (
          <BriefTeachingCopy>
            Track an opportunity, agency, or vendor and we'll surface every change here — new
            amendments, Q&amp;A cutoffs, award decisions, protests, sanctions hits — the moment they
            happen.
          </BriefTeachingCopy>
        ) : (
          <SinceLastLookedList rows={brief.data?.since_last_looked ?? []} />
        )}
      </BriefStrip>

      <BriefStrip
        label="ON YOUR RADAR THIS WEEK"
        countSuffix={brief.isEmpty.oyr ? null : `${brief.data?.on_your_radar.length ?? 0} closing`}
      >
        {brief.isLoading ? (
          <BriefTeachingCopy>loading…</BriefTeachingCopy>
        ) : brief.isEmpty.oyr ? (
          <BriefTeachingCopy>
            Add an opportunity to your pipeline and we'll show every deadline within 14 days here,
            sorted by what's worth most to you.
          </BriefTeachingCopy>
        ) : (
          <RadarList rows={brief.data?.on_your_radar ?? []} />
        )}
      </BriefStrip>

      <BriefStrip label="MARKET PULSE">
        {brief.isEmpty.mp ? (
          <BriefTeachingCopy>
            Tell us your NAICS codes and target agencies and this strip becomes your daily signal
            feed — large awards unsealed, major protests filed, new OTA solicitations, sanctions
            actions affecting primes you compete with.
          </BriefTeachingCopy>
        ) : (
          // Phase 4 will populate this from intel_relevance.
          <BriefTeachingCopy>
            Top-ranked signals matching your focus will appear here.
          </BriefTeachingCopy>
        )}
      </BriefStrip>

      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <Link to="/horizon" className="btn xs gh">
          Browse opportunities
        </Link>
        <Link to="/settings/focus" className="btn xs gh">
          Set up market profile
        </Link>
      </div>
    </div>
  );
}

function BriefStrip({
  label,
  countSuffix,
  children,
}: {
  label: string;
  countSuffix?: string | null;
  children: ReactNode;
}) {
  return (
    <div>
      <div
        className="lbl"
        style={{
          marginBottom: 6,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <span>{label}</span>
        {countSuffix && <span className="meta mono dim2">{countSuffix}</span>}
      </div>
      {children}
    </div>
  );
}

function BriefTeachingCopy({ children }: { children: ReactNode }) {
  return (
    <div className="meta mono" style={{ lineHeight: 1.55 }}>
      {children}
    </div>
  );
}

function RadarList({ rows }: { rows: OnYourRadarRow[] }) {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((r) => {
        const days = daysUntil(r.closing_date);
        const tone = days == null ? 'dim' : days < 3 ? 'rd' : days < 7 ? 'amb' : 'dim';
        return (
          <button
            type="button"
            key={r.pipeline_item_id}
            onClick={() => navigate({ to: '/brief/$id', params: { id: r.entity_id } })}
            style={{
              all: 'unset',
              cursor: 'pointer',
              display: 'grid',
              gridTemplateColumns: '36px 1fr auto',
              gap: 10,
              alignItems: 'baseline',
              textAlign: 'left',
              width: '100%',
            }}
          >
            <span className={`mn ${tone}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {days != null ? `${days}D` : '—'}
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: 12,
                }}
              >
                {r.title ?? '—'}
              </div>
              <div className="meta mono dim" style={{ fontSize: 10 }}>
                {r.agency_raw_name ?? r.jurisdiction ?? '—'}
              </div>
            </div>
            <span className="mn" style={{ fontSize: 11 }}>
              {r.estimated_value != null ? compactUsd(r.estimated_value) : '—'}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((t - Date.now()) / 86_400_000));
}

// ── Since You Last Looked — change feed for tracked entities ───────────────
// Per Iron Rule #11: kept screen-local. Second consumer (an entity-detail
// "recent changes" pane, say) hasn't shown up yet; when it does, lift to
// packages/ui then.
//
// Row layout (32px line):
//   ┌────┬───────────────────┬─────────────────────────────┬──────┐
//   │ABC │ tender_id / name  │ "Amendment #001 posted"     │ 2d   │
//   └────┴───────────────────┴─────────────────────────────┴──────┘
//   chip   entity_title (fg-1)   change_summary (meta dim)    time
//
// The entity title comes from the server (W3-fix). Without it the rail
// looked like teaching copy because the operator couldn't tell *which*
// tracked item the row was talking about.

const CHANGE_KIND_LABEL: Record<string, string> = {
  amendment: 'AMD',
  closed: 'END',
  awarded: 'WON',
  new_tender: 'NEW',
  new_award: 'AWD',
  won_award: 'WIN',
};

// Map change-kind → CSS token class. State-as-color: amber for "needs a
// glance" (amendment), green for wins (awarded/won_award/new_award),
// cyan for net-new (new_tender), dim for terminal-but-uninteresting
// (closed without award).
const CHANGE_KIND_TONE: Record<string, string> = {
  amendment: 'amb',
  closed: 'dim',
  awarded: 'grn',
  new_tender: 'cyn',
  new_award: 'grn',
  won_award: 'grn',
};

function SinceLastLookedList({ rows }: { rows: SinceLastLookedRow[] }) {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {rows.map((r, idx) => {
        const label = CHANGE_KIND_LABEL[r.change_kind] ?? r.change_kind.slice(0, 3).toUpperCase();
        const tone = CHANGE_KIND_TONE[r.change_kind] ?? 'dim';
        const onOpen = () => {
          // tender → brief page; other types currently don't have a deep
          // link, so the row is rendered but doesn't navigate.
          if (r.entity_type === 'tender') {
            navigate({ to: '/brief/$id', params: { id: r.entity_id } });
          }
        };
        const clickable = r.entity_type === 'tender';
        const title = r.entity_title ?? (r.entity_type === 'tender' ? '(untitled)' : '—');
        return (
          <button
            type="button"
            key={`${r.entity_type}:${r.entity_id}:${r.change_kind}:${idx}`}
            onClick={onOpen}
            disabled={!clickable}
            aria-label={`${r.change_kind} on ${title}`}
            className="sll-row"
            style={{
              all: 'unset',
              cursor: clickable ? 'pointer' : 'default',
              display: 'grid',
              gridTemplateColumns: '44px minmax(0, auto) minmax(0, 1fr) auto',
              gap: 10,
              alignItems: 'baseline',
              textAlign: 'left',
              width: '100%',
              minHeight: 32,
              padding: '4px 6px',
              borderLeft: '2px solid transparent',
              borderRadius: 2,
            }}
            onMouseEnter={(e) => {
              if (clickable) {
                e.currentTarget.style.background = 'var(--bg-row-hover)';
                e.currentTarget.style.borderLeftColor = `var(--${tone === 'amb' ? 'amber' : tone === 'grn' ? 'green' : tone === 'cyn' ? 'cyan' : 'bd-strong'})`;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderLeftColor = 'transparent';
            }}
          >
            <span
              className={`mn ${tone}`}
              style={{
                fontSize: 10,
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 600,
                letterSpacing: 0.6,
                padding: '2px 6px',
                border: `1px solid var(--${tone === 'amb' ? 'amber-line' : tone === 'grn' ? 'green-line' : tone === 'cyn' ? 'cyan-line' : 'bd-strong'})`,
                background: `var(--${tone === 'amb' ? 'amber-soft' : tone === 'grn' ? 'green-soft' : tone === 'cyn' ? 'cyan-soft' : 'bd-soft'}, transparent)`,
                textAlign: 'center',
              }}
            >
              {label}
            </span>
            <span
              className="mono"
              style={{
                fontSize: 12,
                color: 'var(--fg-1)',
                fontWeight: 500,
                maxWidth: 240,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={title}
            >
              {title}
            </span>
            <span
              className="dim"
              style={{
                fontSize: 12,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={r.change_summary ?? ''}
            >
              {r.change_summary ?? '—'}
            </span>
            <span className="mn dim2" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
              {relTime(r.ts) ?? '—'}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Saved Screens — top-right ──────────────────────────────────────────────
//
// W4-B: read-only mount point for the platform SavedView primitive's data.
// Rows are clickable: each row navigates to the canonical surface for the
// saved filter (Intel surface for INTEL_SIGNAL-shaped filters, /horizon for
// everything else) with the FilterState encoded into the `f` URL param. The
// CRUD lives on the surface itself via <SavedView/> (mounted on Horizon /
// Awards / Closing-soon / Intel header right-slot) — never here.
//
// Heuristic for routing: Intel-shaped filters have at least one of
// `signal_type`, `severity_bucket`, `time_window`, or an AOI polygon. The
// server doesn't yet stamp an `entity` on each saved screen, so detection is
// structural. When the API gains entity-typing (Phase D-1) this becomes a
// direct field read.
export function isIntelShapedFilters(filters: Record<string, unknown>): boolean {
  if ('signal_type' in filters) return true;
  if ('severity_bucket' in filters) return true;
  if ('time_window' in filters) return true;
  const aoi = filters.aoi as { polygons?: unknown[] } | undefined;
  if (aoi && Array.isArray(aoi.polygons) && aoi.polygons.length > 0) return true;
  return false;
}

function SavedScreensPane({
  query,
}: {
  query: { data?: { items: SavedScreen[] }; isLoading: boolean };
}) {
  const navigate = useNavigate();
  const items = query.data?.items ?? [];
  const openScreen = (filters: Record<string, unknown>) => {
    // Cast: SavedScreen.filters is typed as Record<string, unknown> on the
    // landing-page interface but the wire shape is contracts.FilterState. The
    // encode helper handles the validation internally; we only forward the
    // base64url payload when the encoding fits inline (spill path is server-
    // backed and not reachable for landing-page navigation).
    const enc = encode(filters as f.FilterState);
    const fParam = enc.kind === 'inline' ? enc.value || undefined : undefined;
    if (isIntelShapedFilters(filters)) {
      navigate({ to: '/intel', search: { f: fParam } });
    } else {
      navigate({ to: '/horizon', search: { f: fParam } });
    }
  };
  return (
    <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
      {query.isLoading ? (
        <div className="meta mono" style={{ padding: 16 }}>
          loading…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          glyph="OPP"
          reason="No saved screens yet"
          detail="Save a filter set on /horizon, /intel, or /awards to surface here."
        />
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>NAME</th>
              <th>SHARED</th>
              <th className="r">LAST RUN</th>
              <th className="r">HITS</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr
                key={s.id}
                onClick={() => openScreen(s.filters)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openScreen(s.filters);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`open saved screen ${s.name}`}
                style={{ cursor: 'pointer' }}
                title="open saved screen"
              >
                <td>{s.name}</td>
                <td>
                  {s.is_shared ? (
                    <Pill tone="cyan" dot={false}>
                      SHARED
                    </Pill>
                  ) : (
                    <span className="dim2">—</span>
                  )}
                </td>
                <td className="r mn dim">{relTime(s.last_run_at) ?? '—'}</td>
                <td className="r mn">
                  {s.last_result_count != null ? ts(s.last_result_count) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Recent Awards — bottom-left ────────────────────────────────────────────
function RecentAwardsPane({
  query,
}: {
  query: { data?: { items: AwardRow[] }; isLoading: boolean };
}) {
  const items = query.data?.items ?? [];
  return (
    <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
      {query.isLoading ? (
        <div className="meta mono" style={{ padding: 16 }}>
          loading…
        </div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>DATE</th>
              <th>AGENCY → VENDOR</th>
              <th>JX</th>
              <th className="r">VALUE</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => {
              const value = a.total_contract_value ?? a.contract_value;
              return (
                <tr key={a.id}>
                  <td className="mn dim">{dateOnly(a.publication_date)}</td>
                  <td>
                    <span className="dim">{truncate(a.agency_raw_name, 22)}</span>
                    <span className="dim2 mono"> → </span>
                    <span>{truncate(a.vendor_name, 22)}</span>
                  </td>
                  <td className="mn">{a.jurisdiction}</td>
                  <td className="r mn">{value != null ? compactUsd(value) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Pipeline Summary — bottom-right ────────────────────────────────────────
function PipelinePane({
  stats,
  pipeline,
}: {
  stats?: StatsOverview;
  pipeline?: PipelineStats;
}) {
  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 16, minHeight: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
        <Tile label="CLOSING IN 30D" value={ts(pipeline?.closing_30d ?? 0)} />
        <Tile label="CLOSING IN 90D" value={ts(pipeline?.closing_90d ?? 0)} />
        <Tile label="STANDING OFFERS" value={ts(stats?.totals.standing_offers ?? 0)} />
        <Tile label="DISCLOSURES" value={ts(stats?.totals.disclosures ?? 0)} />
      </div>

      <div className="lbl" style={{ marginBottom: 6 }}>
        BY JURISDICTION
      </div>
      <table className="tbl" style={{ marginBottom: 18 }}>
        <thead>
          <tr>
            <th>JX</th>
            <th className="r">RECORDS</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(stats?.by_jurisdiction ?? []).slice(0, 8).map((j) => {
            const max = stats?.by_jurisdiction?.[0]?.count ?? 1;
            const w = Math.max(2, Math.round((j.count / max) * 100));
            return (
              <tr key={j.jurisdiction}>
                <td className="mn">{j.jurisdiction}</td>
                <td className="r mn">{ts(j.count)}</td>
                <td>
                  <span
                    style={{
                      display: 'inline-block',
                      width: `${w}%`,
                      height: 4,
                      background: 'var(--primary)',
                      verticalAlign: 'middle',
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {pipeline?.newest_award && (
        <div className="meta mono" style={{ marginTop: 12 }}>
          newest award fetched · {dateOnly(pipeline.newest_award)}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ borderLeft: '2px solid var(--bd-strong)', paddingLeft: 10 }}>
      <div className="lbl">{label}</div>
      <div className="metric">{value}</div>
    </div>
  );
}

function truncate(s: string | null | undefined, n = 30): string {
  if (!s) return '—';
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}
